import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Home as House, Search as MagnifyingGlass, Calendar, User, LogIn as SignIn, Plus, LogOut as SignOut, Compass, Users, MessageCircle, Coins, LineChart, Store, Bell, Siren, Info, HeartHandshake, LayoutGrid, PlusCircle, LayoutDashboard, ShieldCheck, Heart, PlayCircle, Package, Calculator, Inbox as InboxIcon, MapPin } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import CityChanger from "@/components/CityChanger";
import ShareButton from "@/components/ShareButton";
import MoreNavMenu from "@/components/MoreNavMenu";
import VoiceSearchMic from "@/components/VoiceSearchMic";
import GeoDeniedBanner from "@/components/GeoDeniedBanner";
import PendingSyncBadge from "@/components/PendingSyncBadge";
import { useI18n } from "@/context/I18nContext";
import AppDock from "@/components/AppDock";
import { LocaleButton } from "@/components/LocalePicker";
import { HreflangLinks } from "@/context/I18nContext";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function loginRedirect() {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  useEffect(() => {
    // Emergency focus mode: floating FABs never cover SOS / ambulance controls.
    document.body.classList.toggle("f2n-focus-mode", /^\/(sos|ambulance|emergency)(\/|$)/.test(loc.pathname));
  }, [loc.pathname]);
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const { t } = useI18n();

  // Section 6 primary nav — 6 tabs · Home · Find · Map · Market · Connect · Me
  // The AppDock at the bottom carries the MRU. Old routes remain via redirects.
  const tabs = [
    { to: "/",        label: t("nav.home"),    icon: House,             id: "home" },
    { to: "/find",    label: t("nav.find"),    icon: MagnifyingGlass,   id: "find" },
    { to: "/map",     label: t("nav.map"),     icon: MapPin,            id: "map" },
    { to: "/market",  label: t("nav.market"),  icon: Store,             id: "market" },
    { to: "/connect", label: t("nav.connect"), icon: MessageCircle,     id: "connect" },
    { to: "/me",      label: t("nav.me"),      icon: User,              id: "me" },
  ];

  return (
    <div className="App min-h-screen bg-stone-50 pb-44 md:pb-0">
      {/* Global tagline strip */}
      <div
        className="w-full bg-[#2D5A27] text-white text-[11px] sm:text-xs font-semibold tracking-wide overflow-hidden"
        data-testid="global-tagline-strip"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-1.5 flex items-center justify-center gap-2 whitespace-nowrap overflow-hidden">
          <span className="w-1.5 h-1.5 rounded-full bg-[#DDA77B] animate-pulse shrink-0" />
          <span className="truncate">
            {t("shell.tagline")}
          </span>
          <span className="w-1.5 h-1.5 rounded-full bg-[#DDA77B] animate-pulse shrink-0" />
        </div>
      </div>

      {/* Top navigation */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-stone-50/85 border-b border-stone-200/60">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group" data-testid="brand-link">
            <Logo size={40} showText tagline />
          </Link>

          <nav className="hidden md:flex items-center gap-1 text-stone-700" data-testid="nav-icons">
            {/* P1a · Section 6 primary navigation — Home / Find / Map /
                Market / Connect / Me. Old destinations remain reachable
                via the AppDock's More drawer + legacy redirect routes. */}
            {tabs.slice(1).map(tab => (
              <NavIcon key={tab.id} to={tab.to} Icon={tab.icon} label={tab.label} testid={`nav-${tab.id}`} />
            ))}
            <InboxNavIcon />
            <MoreNavMenu />
            {user?.is_admin && (
              <NavIcon to="/ops" Icon={ShieldCheck} label={t("shell.ops")} testid="nav-ops" className="text-[#2D5A27] hover:text-[#E05E36]" pulse pulseColor="#2D5A27" />
            )}
          </nav>

          <div className="flex items-center gap-2">
            <div className="hidden lg:block">
              <CityChanger variant="header" />
            </div>
            <div className="hidden md:block">
              <ShareButton
                url="/"
                title="Find2Near — Vocal for Local"
                text="Discover trusted local pros, doctors, jobs, matches & more near you on Find2Near. Join the neighbourhood 👇"
                variant="icon"
                label={t("shell.invite")}
                testIdSuffix="global"
              />
            </div>
            <LocaleButton />
            <Button
              onClick={() => navigate("/post")}
              className="hidden sm:inline-flex rounded-full bg-[#2D5A27] hover:bg-[#24491f] text-white gap-2"
              data-testid="header-post-btn"
            >
              <Plus size={18} /> {t("shell.post")}
            </Button>
            {user ? (
              <div className="relative">
                <button
                  onClick={() => setMenuOpen(v => !v)}
                  className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full border border-stone-200 bg-white hover:border-stone-300"
                  data-testid="user-menu-btn"
                >
                  <div className="w-8 h-8 rounded-full bg-[#E05E36] text-white grid place-items-center text-sm font-bold overflow-hidden">
                    {user.picture ? <img loading="lazy" src={user.picture} alt="" className="w-full h-full object-cover" /> : (user.name?.[0]?.toUpperCase() || "U")}
                  </div>
                  <span className="text-sm font-semibold text-stone-800 hidden sm:block">{user.name?.split(" ")[0]}</span>
                </button>
                {menuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white border border-stone-200 rounded-xl shadow-lg py-2 z-50" onMouseLeave={() => setMenuOpen(false)}>
                    <Link to="/dashboard" className="block px-4 py-2 text-sm hover:bg-stone-50" data-testid="menu-dashboard">{t("shell.my_dashboard")}</Link>
                    <Link to="/account" className="block px-4 py-2 text-sm hover:bg-stone-50" data-testid="menu-account">{t("shell.account")}</Link>
                    <button
                      onClick={() => { logout(); setMenuOpen(false); navigate("/"); }}
                      className="w-full text-left px-4 py-2 text-sm hover:bg-stone-50 text-red-600 flex items-center gap-2"
                      data-testid="menu-logout"
                    >
                      <SignOut size={16} /> {t("shell.logout")}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Button
                onClick={loginRedirect}
                className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a] text-white gap-2"
                data-testid="login-btn"
              >
                <SignIn size={18} /> <span className="hidden sm:inline">{t("shell.login")}</span>
              </Button>
            )}
          </div>
        </div>
      </header>

      <GeoDeniedBanner />
      <PendingSyncBadge />

      <main>{children}</main>
      <VoiceSearchMic />

      {/* Site-wide legal footer — copyright, trademark & patent-pending notice
          with a link to /legal for the full text. Keeps the IP claim visible
          on every page (deterrent + real notice). */}
      <footer className="hidden md:block border-t border-stone-200 bg-stone-50 mt-8" data-testid="site-footer">
        <div className="max-w-7xl mx-auto px-6 py-4 text-[11px] text-stone-500 flex flex-wrap items-center gap-x-4 gap-y-1 justify-between">
          <div>
            © {new Date().getFullYear()} <b className="text-stone-700">find2near</b> · <span lang="en">{t("legal.rights_notice")}</span>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <Link to="/notices" className="hover:text-[#E05E36]">{t("shell.notices")}</Link>
            <Link to="/laws"    className="hover:text-emerald-700 font-bold">{t("shell.laws")}</Link>
            <Link to="/faqs"    className="hover:text-sky-700">{t("shell.faqs")}</Link>
            <Link to="/entertainment" className="hover:text-fuchsia-700">{t("shell.entertainment")}</Link>
            <Link to="/legal"   className="hover:text-red-700 font-bold">{t("shell.legal")}</Link>
            <Link to="/legal-templates" className="hover:text-red-700">{t("shell.templates")}</Link>
            <Link to="/about"   className="hover:text-[#E05E36]">{t("shell.about")}</Link>
            <Link to="/privacy" className="hover:text-[#E05E36]">{t("shell.privacy")}</Link>
            <Link to="/terms"   className="hover:text-[#E05E36]">{t("shell.terms")}</Link>
          </div>
        </div>
      </footer>

      {/* Mobile-app-style bottom dock (Slice 11) — always visible on every
          screen size, showing Home + the user's 5 most-recently-used apps
          + a More drawer. Replaces the older 5-tab mobile bar. */}
      <HreflangLinks />
      <AppDock />
    </div>
  );
}

export { loginRedirect };

/**
 * Icon-only nav button with a tooltip that appears on hover. Uses the app-like mobile
 * pattern of icons in the header, so we can fit 15+ destinations without wrapping.
 */
function NavIcon({ to, Icon, label, testid, className = "", pulse = false, pulseColor = "#DDA77B" }) {
  return (
    <Link
      to={to}
      className={`group relative flex items-center justify-center w-10 h-10 rounded-full hover:bg-stone-100 transition-colors ${className || "hover:text-[#E05E36]"}`}
      data-testid={testid}
      aria-label={label}
    >
      <Icon size={20} />
      {pulse && (
        <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full animate-pulse" style={{ background: pulseColor }} />
      )}
      {/* Tooltip */}
      <span className="pointer-events-none absolute top-full mt-1 left-1/2 -translate-x-1/2 whitespace-nowrap text-[11px] font-semibold bg-stone-900 text-white px-2 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity z-50">
        {label}
      </span>
    </Link>
  );
}

/**
 * Inbox nav icon with a live unread badge — polls /api/inbox/unread-count every 30s.
 * Uses a localStorage cursor so the count is "new since I last opened the inbox".
 */
function InboxNavIcon() {
  const { user } = useAuth();
  const [unread, setUnread] = useState(0);
  const { t } = useI18n();

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api.get("/inbox/unified-counts");   // unread messages + unread service/payment/trip alerts
        if (!cancelled) setUnread(r.data?.total || 0);
      } catch { /* silent — badge just won't update */ }
    };
    tick();
    const iv = setInterval(tick, 30_000);
    // Also refresh whenever the tab regains focus.
    const onFocus = () => tick();
    const onCounts = (e) => { if (!cancelled) setUnread(e.detail?.total || 0); };   // inbox page already fetched them
    window.addEventListener("focus", onFocus);
    window.addEventListener("inbox:changed", onFocus);
    window.addEventListener("inbox:counts", onCounts);
    return () => {
      cancelled = true; clearInterval(iv);
      window.removeEventListener("focus", onFocus); window.removeEventListener("inbox:changed", onFocus); window.removeEventListener("inbox:counts", onCounts);
    };
  }, [user?.user_id]);

  return (
    <Link
      to="/inbox"
      className="group relative flex items-center justify-center w-10 h-10 rounded-full hover:bg-stone-100 hover:text-[#E05E36] transition-colors"
      data-testid="nav-inbox"
      aria-label={t("shell.inbox")}
    >
      <InboxIcon size={20} />
      {unread > 0 && (
        <span
          className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#E05E36] text-white text-[10px] font-black font-mono grid place-items-center border-2 border-stone-50 animate-in fade-in zoom-in duration-300"
          data-testid="inbox-unread-badge"
        >
          {unread > 99 ? "99+" : unread}
        </span>
      )}
      <span className="pointer-events-none absolute top-full mt-1 left-1/2 -translate-x-1/2 whitespace-nowrap text-[11px] font-semibold bg-stone-900 text-white px-2 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity z-50">
        {t("shell.inbox")} {unread > 0 && `· ${t("shell.inbox_new", { n: unread })}`}
      </span>
    </Link>
  );
}
